import { Popup } from '@/components/common/Popup';
import type { MinimalTarget } from '@/generated/model';
import { useIsPhoneOrHighScreen } from '@/shared/hooks/useIsPhoneOrHighScreen';

import { useRefuelsQuery } from '../../api/useRefuelsQuery';
import { useRefuelsMobileNavigation } from '../../hooks/useRefuelsMobileNavigation';
import { useRefuelsSelection } from '../../hooks/useRefuelsSelection';
import { getRefuelDateSelectView } from '../../utils/refuelDateSelectView';
import { getDefaultRefuelsDateRange, isSameDateRange } from '../../utils/refuelsDateUtils';
import { getExportFileName } from '../../utils/refuelsExport';
import { canNavigateToResult } from '../../utils/refuelsNavigation';
import { getRefuelsResult } from '../../utils/refuelsResult';
import { RefuelHeader } from '../refuel-header/RefuelHeader';
import { RefuelsMobileLayout } from '../refuels-mobile-layout/RefuelsMobileLayout';
import { RefuelsDesktopLayout } from '../refuels-desktop-layout/RefuelsDesktopLayout';

type RefuelsPopupProps = {
  closePopup: () => Promise<void>;
};

export default function RefuelsPopup({ closePopup }: RefuelsPopupProps) {
  const { selectedTarget, selectedDates, selectTarget, selectDates, requestNewVehicle, requestNewDate } =
    useRefuelsSelection();
  const isPhone = useIsPhoneOrHighScreen();

  const refuelsQuery = useRefuelsQuery({
    targetId: selectedTarget?.id,
    dates: selectedDates,
  });
  const result = getRefuelsResult(refuelsQuery.data, selectedDates);
  const { tab, hasSubmittedDates, changeTab, onTargetSelected, onDatesSubmitted, onNewVehicle, onNewDate } =
    useRefuelsMobileNavigation({
      result,
      isFetching: refuelsQuery.isFetching,
      isSuccess: refuelsQuery.isSuccess,
    });
  const exportFileName = getExportFileName(selectedTarget, selectedDates);
  const hasNoResult =
    hasSubmittedDates && refuelsQuery.isSuccess && !refuelsQuery.isFetching && result?.refuels.length === 0;
  const dateSelectView = getRefuelDateSelectView({
    isError: refuelsQuery.isError,
    hasSelectedDates: selectedDates !== undefined,
    hasNoResult,
  });
  const canGoForward = canNavigateToResult({
    hasResult: result !== undefined && result.refuels.length > 0,
    isFetching: refuelsQuery.isFetching,
    tab,
  });

  /*
   * These handlers must update selection and mobile navigation together. Dispatch navigation first so its flag or tab
   * is ready before the query key changes; desktop-only handlers below update selection only.
   */
  function handleTargetSelect(target: MinimalTarget) {
    onTargetSelected();
    selectTarget(target);
  }

  function handleDatesSelect(from: Date, to: Date) {
    if (!selectedTarget) {
      return;
    }

    onDatesSubmitted();

    if (isSameDateRange(selectedDates, { from, to })) {
      void refuelsQuery.refetch();
      return;
    }

    selectDates({ from, to });
  }

  function handleChooseNewVehicle() {
    onNewVehicle();
    requestNewVehicle();
  }

  function handleChooseNewDate() {
    onNewDate();
    requestNewDate();
  }

  function handleDesktopTargetSelect(target: MinimalTarget) {
    selectTarget(target, selectedDates ?? getDefaultRefuelsDateRange());
  }

  function handleDesktopDatesSelect(from: Date, to: Date) {
    selectDates({ from, to });
  }

  function retryRefuels() {
    void refuelsQuery.refetch();
  }

  return isPhone ? (
    <Popup
      popupTitle={undefined}
      header={
        <RefuelHeader
          tab={tab}
          selectedTarget={selectedTarget}
          selectedDates={selectedDates}
          canGoForward={canGoForward}
          onTabChange={changeTab}
        />
      }
      body={
        <RefuelsMobileLayout
          result={result}
          isFetching={refuelsQuery.isFetching}
          onRetry={retryRefuels}
          tab={tab}
          selectedTarget={selectedTarget}
          exportFileName={exportFileName}
          view={dateSelectView}
          onDatesSelect={handleDatesSelect}
          onChooseNewDate={handleChooseNewDate}
          onChooseNewVehicle={handleChooseNewVehicle}
          onTargetSelect={handleTargetSelect}
        />
      }
      footer={undefined}
      closePopup={closePopup}
      showCloseButton={false}
      isFullScr={true}
    />
  ) : (
    <RefuelsDesktopLayout
      result={result}
      isFetching={refuelsQuery.isFetching}
      onRetry={retryRefuels}
      hasBlockingError={refuelsQuery.isError && refuelsQuery.data === undefined}
      selectedTarget={selectedTarget}
      selectedDates={selectedDates}
      onTargetSelect={handleDesktopTargetSelect}
      onDatesSelect={handleDesktopDatesSelect}
      exportFileName={exportFileName}
    />
  );
}
